// HOOKS
import useCreateShipment from "../../../hooks/shipments/useCreate.shipment";

import DefaultButton from "../../buttons/default.button";
import { useContext, useEffect, useState } from "react";
import { SelectedTicketsContext } from "../../../contexts/tickets/selectedTickets.context";
import useReturnFormComponents from "../../../hooks/other/useReturnFormComponents";
import SplitMultiSelectInput from "../../inputs/selections/multi/split.multi.select.input";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/refresh.icon.svg";
import { ReactComponent as SwapIcon } from "../../../assets/icons/swap.icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/delete.icon.svg";
import TicketShipmentMultiSelectInput from "../../inputs/selections/multi/shipments/ticket.shipment.multi.select.input";
// CONSTANTS
import { US_STATES } from "../../../assets/variables/usStates";
import { UPS_SERVICES } from "../../../assets/variables/upsServices";
import { UPS_PACKAGING_CODES } from "../../../assets/variables/upsPackagingCodes";

const normalizeState = (stateValue) => {
    if (!stateValue) return "";
    const parts = stateValue.split(",");
    const normalized = parts[0].trim().toUpperCase();
    if (normalized.length === 2) {
      if (US_STATES.some(state => state.value === normalized)) {
        return normalized;
      }
    }

    const stateMatch = US_STATES.find(state => state.label.toUpperCase() === stateValue.toUpperCase());
    if (stateMatch) {
        return stateMatch.value;
    }
    
    return normalized.length > 2 ? normalized.substring(0, 2) : normalized;
  };

const DEFAULT_FORM_FIELDS = {
  from: {
    name: "Shipping Dept.",
    attention_name: "",
    company_displayable_name: "Techcycle Solutions", // The company_displayable_name will be displayed in tracking results and notification messages in place of the name associated with the shipper account.
    address1: "6864 W 153rd St",
    address2: "",
    city: "Overland Park",
    state: "KS",
    zip: "66223",
    phone: "9137177779",
    contact_id: null,
  },
  to: {
    name: "",
    attention_name: "",
    company_displayable_name: "", // The company_displayable_name will be displayed in tracking results and notification messages in place of the name associated with the shipper account.
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    contact_id: null,
    reference_id: "",
  },
  description: "",
  shipping_account: "G1987A",
  service: "03",
  packaging: {
    packaging: "02", // Note: Only packaging type code 02 is applicable to Ground Freight Pricing. Package type 24, or 25 is only allowed for shipment without return service.
    length: "", // 0 to 108 IN or 0 to 270 CM
    width: "", // max 999
    height: "", // max 999
    weight: "",
  },
  customer_id: null,
  contact_id: null,
  tickets: [], // ticket { _id, id, number, customer_business_then_name, invoice_contacts}
};

const NewShipmentForm = () => {
  const { createShipment } = useCreateShipment();
  const { selectedTickets, setSelectedTickets } = useContext(
    SelectedTicketsContext
  );
  const [contacts, setContacts] = useState([]);

  const { getFormComponents } = useReturnFormComponents();
  const [isLoading, setIsLoading] = useState(false);
  const [formFields, setFormFields] = useState(DEFAULT_FORM_FIELDS);
  const {
    from,
    to,
    service,
    packaging,
    tickets,
    shipping_account,
    description,
    customer_id,
  } = formFields;

  // Reset customer_id & contact_id if no tickets are selected
  useEffect(() => {
    if (!tickets?.length && customer_id) {
      setFormFields({
        ...formFields,
        to: DEFAULT_FORM_FIELDS.to,
        customer_id: "",
        contact_id: "",
      });
    }
  }, [tickets]);

  useEffect(() => {
  if (!selectedTickets?.length) {
    setContacts([]);
    return;
  }

  // Gather all contacts (invoice + fallback)
  const allContacts = selectedTickets.reduce((acc, ticket) => {
    if (!ticket) return acc;

    let contacts = [];

    // If invoice contacts exist, take those
    if (Array.isArray(ticket.invoice_contacts) && ticket.invoice_contacts.length > 0) {
      contacts = ticket.invoice_contacts.map((c) => ({
        ...c,
        customer_id: ticket.customer_id,
      }));
    } 
    // Otherwise, fallback to the ticket's main contact
    else if (ticket.contact && ticket.contact.id) {
      const c = ticket.contact;
      contacts = [
        {
          ...c,
          customer_id: ticket.customer_id,
        },
      ];
    }

    if (!contacts?.length) return acc;
    return [...acc, ...contacts];
  }, []);

  // Deduplicate by (id + customer_id)
  const uniqueContacts = allContacts.filter(
    (contact, index, self) =>
      index ===
      self.findIndex(
        (t) =>
          +t.id === +contact.id &&
          +t.customer_id === +contact.customer_id &&
          t.name === contact.name &&
          t.address1 === contact.address1 &&
          t.city === contact.city &&
          t.state === contact.state &&
          t.zip === contact.zip
      )
  );

  const sortedContacts = uniqueContacts.sort((a, b) =>
    a?.name?.localeCompare(b?.name)
  );

  setContacts(sortedContacts);
}, [selectedTickets]);


  const handleChange = (e) => {
    e.preventDefault();

    const { name, value } = e.target;

    // Function to update the state object based on the field path
    const updateFieldByPath = (obj, path, value) => {
      const keys = path.split(".");
      let current = obj;

      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          // Update the field value at the final key in the path
          current[key] = value;
        } else {
          // Traverse to the next level of the object
          current = current[key];
        }
      });
      return { ...obj };
    };

    setFormFields((prevFields) =>
      updateFieldByPath({ ...prevFields }, name, value)
    );
  };

 const handleMultiSelectChange = (e) => {
  e.preventDefault();

  const {
    name,
    id,
    customer_business_then_name,
    reset,
    number,
    contact_id,
    customer_id,
  } = e.target.dataset;

  if (!name) return;

  if (reset) {
    setFormFields({ ...formFields, [name]: [] });
    return;
  }

  const options = formFields[name] || [];

  // Normalize IDs
  const cid = contact_id ? +contact_id : null;
  const custId = customer_id ? +customer_id : null;

  // Try to find a contact (invoice or backup)
  let contact =
    contacts.find((c) => c.id === cid) ||
    selectedTickets.find(
      (t) =>
        +t.customer_id === custId &&
        (t?.contact?.id === cid ||
          (!t?.invoice_contacts?.length && t?.contact))
    )?.contact;

  // Ticket object for reference
  const selectedTicket = selectedTickets.find((t) => +t.id === +id);

  // Create base ticket entry
  const ticketEntry = {
    id: +id,
    number: +number,
    customer_business_then_name,
    contact_id: contact?.id || cid || null,
    customer_id: custId,
  };

  // Toggle selection
  const isSelected = options.some((v) => v.id === +id);
  const updatedOptions = isSelected
    ? options.filter((v) => v.id !== +id)
    : [...options, ticketEntry];

  // If no destination contact yet, populate "to"
  const toContact = formFields.to || {};
  const shouldSetToContact =
    !toContact?.address1 && (contact?.id || cid) && contact;

  setFormFields({
    ...formFields,
    [name]: updatedOptions,
    ...(shouldSetToContact && {
      to: {
        contact_id: contact?.id || cid,
        name: contact?.name || "",
        company_displayable_name:
          customer_business_then_name ||
          selectedTicket?.customer_business_then_name ||
          "",
        address1: contact?.address1 || "",
        address2: contact?.address2 || "",
        city: contact?.city || "",
        state: normalizeState(contact?.state) || "",
        zip: contact?.zip || "",
        phone: contact?.phone || "",
      },
      contact_id: contact?.id || cid,
      customer_id: custId,
    }),
  });
};


  const resetToFields = () => {
    setFormFields({
      ...formFields,
      to: {
        name: "",
        attention_name: "",
        company_displayable_name: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        phone: "",
        contact_id: "",
        reference_id: "",
      },
      customer_id: "",
    });
  };
  const resetFromFields = () => {
    setFormFields({
      ...formFields,
      from: DEFAULT_FORM_FIELDS.from,
    });
  };

  const packageForm = [
    {
      type: "selection",
      props: {
        label: "Packaging",
        value: packaging.packaging,
        name: "packaging.packaging",
        options: UPS_PACKAGING_CODES,
        classNames: { container: "w-96" },
        onChange: handleChange,
      },
    },
    {
      type: "number",
      props: {
        label: "Length",
        value: packaging.length,
        name: "packaging.length",
        onChange: handleChange,
        className: "w-32",
        required: true,
        min: 0,
        max: 999,
      },
    },
    {
      type: "number",
      props: {
        label: "Width",
        value: packaging.width,
        name: "packaging.width",
        onChange: handleChange,
        className: "w-32",
        required: true,
        min: 0,
        max: 999,
      },
    },
    {
      type: "number",
      props: {
        label: "Height",
        value: packaging.height,
        name: "packaging.height",
        onChange: handleChange,
        className: "w-32",
        required: true,
        min: 0,
        max: 999,
      },
    },
    {
      type: "number",
      props: {
        label: "Weight",
        value: packaging.weight,
        name: "packaging.weight",
        onChange: handleChange,
        className: "w-32",
        required: true,
        min: 0,
        max: 999,
      },
    },
  ];

  const shippingDetailsForm = [
    {
      type: "text",
      props: {
        label: "Description",
        value: description,
        name: "description",
        onChange: handleChange,
        className: "w-96",
      },
    },
    {
      type: "text",
      props: {
        label: "Shipping Account",
        value: shipping_account,
        name: "shipping_account",
        onChange: handleChange,
        className: "w-96",
        disabled: true,
      },
    },
    {
      type: "selection",
      props: {
        label: "Service",
        value: service,
        name: "service",
        options: UPS_SERVICES,
        classNames: { container: "w-96" },
        onChange: handleChange,
      },
    },
  ];

  const fromForm = [
    {
      type: "text",
      props: {
        label: "Name",
        value: from?.name,
        name: "from.name",
        onChange: handleChange,
        className: "w-96",
        required: true,
      },
    },
    {
      type: "text",
      props: {
        label: "Attention Name",
        value: from?.attention_name,
        name: "from.attention_name",
        onChange: handleChange,
        className: "w-96",
      },
    },
    {
      type: "text",
      props: {
        label: "Company Displayable Name",
        value: from?.company_displayable_name,
        name: "from.company_displayable_name",
        onChange: handleChange,
        className: "w-96",
      },
    },
    {
      type: "text",
      props: {
        label: "Address 1",
        value: from?.address1,
        name: "from.address1",
        onChange: handleChange,
        className: "w-96",
        required: true,
      },
    },
    {
      type: "text",
      props: {
        label: "Address 2",
        value: from?.address2,
        name: "from.address2",
        onChange: handleChange,
        className: "w-96",
      },
    },
    {
      type: "text",
      props: {
        label: "City",
        value: from?.city,
        name: "from.city",
        onChange: handleChange,
        className: "w-96",
        required: true,
      },
    },
    {
      type: "selection",
      props: {
        label: "State",
        value: from?.state,
        name: "from.state",
        options: US_STATES,
        classNames: { container: "w-96" },
        onChange: handleChange,
        required: true,
      },
    },
    {
      type: "text",
      props: {
        label: "Zip",
        value: from?.zip,
        name: "from.zip",
        onChange: handleChange,
        className: "w-96",
        required: true,
      },
    },
    {
      type: "text",
      props: {
        label: "Phone",
        value: from?.phone,
        name: "from.phone",
        onChange: handleChange,
        className: "w-96",
      },
    },
  ];

  const toForm = [
    {
      type: "text",
      props: {
        label: "Name",
        value: to.name,
        name: "to.name",
        onChange: handleChange,
        className: "w-96",
        autoComplete: "off",
        required: true,
      },
    },
    {
      type: "text",
      props: {
        label: "Attention Name",
        value: to.attention_name,
        name: "to.attention_name",
        onChange: handleChange,
        className: "w-96",
        autoComplete: "off",
      },
    },
    {
      type: "text",
      props: {
        label: "Company Displayable Name",
        value: to.company_displayable_name,
        name: "to.company_displayable_name",
        onChange: handleChange,
        className: "w-96",
        autoComplete: "off",
      },
    },
    {
      type: "text",
      props: {
        label: "Address 1",
        value: to.address1,
        name: "to.address1",
        onChange: handleChange,
        className: "w-96",
        autoComplete: "off",
        required: true,
      },
    },
    {
      type: "text",
      props: {
        label: "Address 2",
        value: to.address2,
        name: "to.address2",
        onChange: handleChange,
        className: "w-96",
        autoComplete: "off",
      },
    },
    {
      type: "text",
      props: {
        label: "City",
        value: to.city,
        name: "to.city",
        onChange: handleChange,
        className: "w-96",
        autoComplete: "off",
        required: true,
      },
    },
    {
      type: "selection",
      props: {
        label: "State",
        value: to.state,
        name: "to.state",
        options: US_STATES,
        classNames: { container: "w-96" },
        onChange: handleChange,
        required: true,
      },
    },
    {
      type: "text",
      props: {
        label: "Zip",
        value: to.zip,
        name: "to.zip",
        onChange: handleChange,
        className: "w-96",
        autoComplete: "off",
        required: true,
      },
    },
    {
      type: "text",
      props: {
        label: "Phone",
        value: to.phone,
        name: "to.phone",
        onChange: handleChange,
        className: "w-96",
        autoComplete: "off",
      },
    },
    {
      type: "text",
      props: {
        label: "Reference ID",
        value: to.reference_id,
        name: "to.reference_id",
        onChange: handleChange,
        className: "w-96",
        autoComplete: "off",
      },
    }
  ];

  const handlePrintLabel = (label) => {
    // Create a new window for printing
    const printWindow = window.open("", "_blank");

    // Check if the window was successfully opened
    if (
      !printWindow ||
      printWindow.closed ||
      typeof printWindow.closed === "undefined"
    ) {
      alert(
        "Popup blocker is preventing the print window from opening. Please allow popups for this site."
      );
      return;
    }

    // Add the GIF image to the new window
    printWindow.document.write(`
      <html>
        <head>
          <title>Shipping Label</title>
        </head>
        <body>
          <img src="data:image/gif;base64,${label}" />
        </body>
      </html>
    `);
    printWindow.document.close();

    // Wait for the image to load, then print with a timeout for safety
    const printTimeout = setTimeout(() => {
      if (printWindow) {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);

    // Listen for the onload event to ensure the image is ready
    printWindow.onload = () => {
      clearTimeout(printTimeout); // Clear the timeout if onload is called
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const REQUIRED_ADDRESS_FIELDS = [
        "name",
        "address1",
        "city",
        "state",
        "zip",
      ];
      const REQUIRED_PACKAGE_FIELDS = ["length", "width", "height", "weight"];
      if (isLoading) return;
      // Validate form fields
      for (const field of REQUIRED_ADDRESS_FIELDS) {
        if (!formFields.from[field]) {
          alert(`Please enter a value for 'From ${field}'`);
          return;
        }
        if (!formFields.to[field]) {
          alert(`Please enter a value for 'To ${field}'`);
          return;
        }
      }
      for (const field of REQUIRED_PACKAGE_FIELDS) {
        if (!formFields.packaging[field]) {
          alert(`Please enter a value for 'Package ${field}'`);
          return;
        }
        if (formFields.packaging[field] <= 0) {
          alert(`Please enter a value greater than 0 for 'Package ${field}'`);
          return;
        }
      }

      setIsLoading(true);
      const res = await createShipment(formFields);
      if (res && !res?.errors) {
        setSelectedTickets([
          ...selectedTickets.filter(
            (ticket) => !tickets.find((t) => t.id === ticket.id)
          ),
        ]);
        setFormFields({
          from: {
            name: "Shipping Dept.",
            attention_name: "",
            company_displayable_name: "Techcycle Solutions",
            address1: "6864 W 153rd St",
            address2: "",
            city: "Overland Park",
            state: "KS",
            zip: "66223",
            phone: "9137177779",
            contact_id: null,
          },
          to: {
            name: "",
            attention_name: "",
            company_displayable_name: "",
            address1: "",
            address2: "",
            city: "",
            state: "",
            zip: "",
            phone: "",
            contact_id: null,
            reference_id: "",
          },
          description: "",
          shipping_account: "G1987A",
          service: "03",
          packaging: {
            packaging: "02",
            length: "",
            width: "",
            height: "",
            weight: "",
          },
          customer_id: null,
          contact_id: null,
          tickets: [],
        });

        if (res?.encoded_label) {
          handlePrintLabel(res.encoded_label);
        }
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error({ error });
      alert(error.message);
    }
  };

  const swapAddressFields = () => {
    setFormFields({
      ...formFields,
      from: { ...to },
      to: { ...from },
    });
  };

  const clearFromFields = () => {
    setFormFields({
      ...formFields,
      from: {
        name: "",
        attention_name: "",
        company_displayable_name: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        phone: "",
        contact_id: null,
        reference_id: ""
      },
    });
  };

  const handleCopyContact = (contact_id) => {
    if (!contact_id) {
      console.warn("%c[handleCopyContact] Missing contact_id.", "color: red;");
      return;
    }

    console.log("%c[handleCopyContact] Copying contact info...", "color: #00b3b3; font-weight: bold;");

    // Find all tickets where the contact matches or fallback applies
    const matchingTickets = selectedTickets.filter((ticket) => {
      const hasInvoiceContact = ticket?.invoice_contacts?.some(
        (c) => c?.id === contact_id
      );
      const hasFallbackContact =
        (!ticket.invoice_contacts?.length && ticket?.contact?.id === contact_id);

      return hasInvoiceContact || hasFallbackContact;
    });

    if (!matchingTickets.length) {
      console.warn("%c[handleCopyContact] No matching tickets found.", "color: red;");
      return;
    }

    // Try to find contact in contacts list first
    let matchingContact = contacts.find((c) => c?.id === contact_id);

    // Fallback: if not found in contacts, use contact from one of the matching tickets
    if (!matchingContact) {
      const fallbackTicket = matchingTickets.find((t) => t?.contact?.id === contact_id);
      if (fallbackTicket?.contact) {
        matchingContact = fallbackTicket.contact;
      } 
    } 

    // 🧩 Apply to form fields
    setFormFields({
      ...formFields,
      tickets: matchingTickets.map((ticket) => ({
        id: ticket.id,
        number: ticket.number,
        customer_business_then_name: ticket.customer_business_then_name,
        contact_id: contact_id,
        customer_id: ticket.customer_id,
      })),
      to: {
        contact_id: matchingContact?.id,
        name: matchingContact?.name,
        company_displayable_name: matchingTickets?.[0]?.customer_business_then_name,
        address1: matchingContact?.address1,
        address2: matchingContact?.address2,
        city: matchingContact?.city,
        state: normalizeState(matchingContact?.state),
        zip: matchingContact?.zip,
        phone: matchingContact?.phone,
      },
      contact_id: contact_id,
      customer_id: matchingTickets?.[0]?.customer_id,
    });

  };

  const handleCopyCustomer = (customer_id, contact_id) => {
    if (!customer_id && !contact_id) {
      return;
    }

    const matchingTickets = selectedTickets.filter(
      (ticket) => +ticket?.customer_id === +customer_id
    );

    if (!matchingTickets.length) {
      return;
    }

    let matchingContact = contacts.find((c) => c?.id === +contact_id);

    if (!matchingContact) {
      const fallbackTicket = matchingTickets.find(
        (t) =>
          (!t.invoice_contacts?.length && t?.contact?.id === +contact_id) ||
          (!t.invoice_contacts?.length && !contact_id)
      );
      if (fallbackTicket?.contact) {
        matchingContact = fallbackTicket.contact;
      } 
    }

    setFormFields({
      ...formFields,
      tickets: matchingTickets.map((ticket) => ({
        id: ticket.id,
        number: ticket.number,
        customer_business_then_name: ticket.customer_business_then_name,
        contact_id: ticket.contact_id,
        customer_id: ticket.customer_id,
      })),
      to: {
        contact_id: matchingContact?.id,
        name: matchingContact?.name,
        company_displayable_name: matchingTickets?.[0]?.customer_business_then_name,
        address1: matchingContact?.address1,
        address2: matchingContact?.address2,
        city: matchingContact?.city,
        state: normalizeState(matchingContact?.state),
        zip: matchingContact?.zip,
        phone: matchingContact?.phone,
      },
      contact_id: matchingContact?.id || contact_id,
      customer_id: customer_id,
    });
  };

  return (
    <form
      autoComplete="off"
      className="w-[90%] border-animate rounded-2xl shadow-lg  flex flex-col items-center gap-4 p-8 bg-gray-200"
    >
      <h1>Create Shipment</h1>

      <div className="flex justify-end w-full px-4 py-2">
        <DefaultButton handleClick={handleSave} loading={isLoading}>
          Create & Print
        </DefaultButton>
      </div>
      <div className="bg-gray-300 p-2 rounded flex flex-wrap gap-4">
        <h2 className="bg-slate-200 p-2 rounded font-bold shadow">
          Shipping Details
        </h2>
        <TicketShipmentMultiSelectInput
          selectedTickets={selectedTickets}
          formFields={formFields}
          contacts={contacts}
          handleMultiSelectChange={handleMultiSelectChange}
          label={"Tickets:"}
          handleCopyContact={handleCopyContact}
          handleCopyCustomer={handleCopyCustomer}
        />
        {getFormComponents({ fields: shippingDetailsForm })}
        <div className="flex flex-wrap gap-4">
          <h2 className="flex items-center bg-slate-200 p-2 rounded font-bold shadow mt-2">
            Package
          </h2>
          {getFormComponents({ fields: packageForm })}
        </div>
      </div>

      <div className="flex flex-wrap gap-4 justify-center">
        <div className="flex flex-col bg-gray-300 p-2 rounded gap-y-2">
          <h2 className="bg-slate-200 p-2 rounded font-bold shadow">From</h2>
          <div className="flex flex-row gap-2 justify-start items-center">
            <RefreshIcon
              onClick={resetFromFields}
              className="flex fill-secondary bg-greenGlass hover:bg-primary rounded p-1 cursor-pointer shadow-sm shadow-black backdrop-blur-sm"
            />
            <SwapIcon
              onClick={swapAddressFields}
              className="flex fill-secondary bg-greenGlass hover:bg-primary rounded p-1 cursor-pointer shadow-sm shadow-black backdrop-blur-sm"
            />
            <DeleteIcon
              onClick={clearFromFields}
              className="flex fill-secondary bg-greenGlass hover:bg-primary rounded p-1 cursor-pointer shadow-sm shadow-black backdrop-blur-sm"
            />
          </div>
          {getFormComponents({ fields: fromForm })}
        </div>
        <div className="flex flex-col bg-gray-300 p-2 rounded gap-y-2">
          <h2 className="bg-slate-200 p-2 rounded font-bold shadow">To</h2>
          <div className="flex flex-row gap-2 justify-start items-center">
            <SwapIcon
              onClick={swapAddressFields}
              className="flex fill-secondary bg-greenGlass hover:bg-primary rounded p-1 cursor-pointer shadow-sm shadow-black backdrop-blur-sm"
            />
            <DeleteIcon
              onClick={resetToFields}
              className="flex fill-secondary bg-greenGlass hover:bg-primary rounded p-1 cursor-pointer shadow-sm shadow-black backdrop-blur-sm"
            />
          </div>
          {getFormComponents({ fields: toForm })}
        </div>
      </div>
    </form>
  );
};

export default NewShipmentForm;